import { CaretDown, CaretUp, Funnel } from '@phosphor-icons/react';
import { getFormatDate } from '../../../shared/utils/dates.util';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useEffect, useRef, useState } from 'react';
import Loading from '../../atoms/Loading';
import { MonthlyDeclarationOverviewItem } from '../../../@types/TaxProfile';
import ModalPresentedDeclaration from './ModalPresentedDeclaration';
import DeclarationOverviewItem from './DeclarationOverviewItem';
import PurchaseDeclaration from './PurchaseDeclaration';
import useIsElementVisible from '../../../hooks/useElementIsVisible'; 
import AnnualPreviewHome from '../../../pages/home/organisms/AnnualPreviewHome';

export enum DeclarationOverviewStatus {
	PRESENTED = 'Declaración presentada',
	MISSING = 'Declaración faltante',
	MISSING_WITHOUT_INCOME = 'Sin ingresos pero con obligación de presentar en 0s',
}

export const Filters = ({
	yearsArray,
	selectedYear,
	setSelectedYear,
	setSelectedStatus,
	setSelectedType,
	selectedType,
	setSelectedDeclarations,
	setShowFilters,
	className,
}: {
	yearsArray: { value?: string; label?: string }[];
	selectedYear: string;
	setSelectedYear: (year: string) => void;
	selectedStatus: string;
	setSelectedStatus: (status: string) => void;
	setSelectedType: (type: string) => void;
	selectedType: string;
	setSelectedDeclarations: (
		declarations: MonthlyDeclarationOverviewItem[],
	) => void;
	className?: string;
	setShowFilters: (show: boolean) => void;
}) => {
	return (
		<div className={`flex items-center gap-2 ${className}`}>
			<select
				className='bg-white text-sm border disabled:opacity-50 border-neutral-200 rounded-xl cursor-pointer py-1 px-2 outline-none h-9 w-full md:w-60 truncate md:max-w-none'
				value={selectedType}
				onChange={(e) => {
					setSelectedType(e.target.value);
					setSelectedDeclarations([]);
					setShowFilters(false);
					setSelectedYear('all');
				}}
			>
				{[
					{ value: 'annual', label: 'Declaraciones anuales' },
					{ value: 'platec', label: 'Plataformas tecnológicas' },
					{ value: 'resico', label: 'RESICO' },
				].map((status) => (
					<option
						key={status.value}
						value={status.value}
					>
						{status.label}
					</option>
				))}
			</select>

			{yearsArray.length > 0 && selectedType !== 'annual' && (
				<select
					className='w-full bg-white text-sm border border-neutral-200 rounded-xl cursor-pointer h-9 px-1 md:px-3 outline-none'
					value={selectedYear || ''}
					onChange={(e) => {
						setSelectedYear(e.target.value);
						setSelectedStatus('all');
						setSelectedDeclarations([]);
						setShowFilters(false);
					}}
				>
					{yearsArray.map((year) => (
						<option
							key={year.value}
							value={year.value}
						>
							{year.label}
						</option>
					))}
				</select>
			)}
		</div>
	);
};

export const DeclarationsOverview = ({
	page = 'home',
}: {
	page?: 'home' | 'annual' | 'taxboard' | 'imssRegulations';
}) => {
	const { isLoading } = useSelector((state: RootState) => state.tax);

	const [selectedPresentedDeclaration, setSelectedPresentedDeclaration] =
		useState<MonthlyDeclarationOverviewItem | null>(null);
	const [selectedYear, setSelectedYear] = useState<string>(
		page === 'annual' ? '2024' : 'all',
	);
	const [selectedStatus, setSelectedStatus] = useState('all');
	const [selectedType, setSelectedType] = useState('platec');
	const [showAllDeclarations, setShowAllDeclarations] = useState(false);
	const [selectedDeclarations, setSelectedDeclarations] = useState<
		MonthlyDeclarationOverviewItem[]
	>([]);
	const [showFilters, setShowFilters] = useState(false);
	const declarationsOverviewDetail = useSelector(
		(state: RootState) => state.tax.declarationsOverviewDetail,
	);
	const declarationsOverviewRef = useRef<HTMLDivElement>(null);

	const isDeclarationsOverviewRefVisible = useIsElementVisible(
		declarationsOverviewRef,
	);

	const selectedTypeDeclarations = declarationsOverviewDetail?.filter(
		(declaration) => declaration.type === selectedType,
	);

	const availableYears = new Set(
		selectedTypeDeclarations?.map(
			(declaration) => declaration.declaration_period?.split('-')[1],
		),
	);

	const yearsArray = [
		{ value: 'all', label: 'Más recientes' },
		...Array.from(availableYears)
			.sort((a, b) => Number(b) - Number(a))
			.map((year) => ({ value: year, label: year })),
	];

	const filteredDeclarations =
		selectedTypeDeclarations?.filter((declaration) => {
			const declarationYear =
				declaration?.type === 'annual'
					? declaration?.declaration_year
					: declaration.declaration_period?.split('-')?.[1];

			if (page === 'taxboard') {
				return declaration.type === 'annual';
			}

			if (page === 'annual') {
				return (
					declaration.type !== 'annual' &&
					declaration.obligation_compliance_check &&
					declarationYear === '2024' &&
					[
						DeclarationOverviewStatus.MISSING_WITHOUT_INCOME,
						DeclarationOverviewStatus.MISSING,
					].includes(declaration.obligation_compliance_check) &&
					declaration?.period_hired_with_heru === 'Period no contratado'
				);
			}

			if (selectedYear !== 'all' && declarationYear !== selectedYear)
				return false;
			if (selectedStatus === 'all') return true;

			if (
				selectedStatus === 'presented' &&
				declaration.obligation_compliance_check ===
					DeclarationOverviewStatus.PRESENTED
			)
				return true;
			if (
				selectedStatus === 'pending' &&
				declaration.obligation_compliance_check ===
					DeclarationOverviewStatus.MISSING
			)
				return true;

			return false;
		}) || [];

	useEffect(() => {
		setSelectedDeclarations([]);
	}, []);

	useEffect(() => {
		if (page === 'taxboard') {
			setSelectedType('annual');
		} else {
			const hasPlatec = declarationsOverviewDetail?.some(
				(declaration) => declaration.type === 'platec',
			);

			const hasResico = declarationsOverviewDetail?.some(
				(declaration) => declaration.type === 'resico',
			);

			if (hasPlatec) setSelectedType('platec');
			if (hasResico) setSelectedType('resico');
			if (!hasPlatec && !hasResico) setSelectedType('annual');
		}
	}, [declarationsOverviewDetail]);

	if (isLoading.declarationsOverviewDetail || isLoading.userIsPlatec) {
		return (
			<Loading className='bg-white rounded-xl shadow-sm p-2 w-full py-4' />
		);
	}

	if (page === 'taxboard') {
		return (
			<div className='p-2 flex flex-col gap-4'>
				<ModalPresentedDeclaration
					selectedPresentedDeclaration={selectedPresentedDeclaration}
					setSelectedPresentedDeclaration={setSelectedPresentedDeclaration}
				/>
				<AnnualPreviewHome page='taxboard' />
				{filteredDeclarations?.length > 0 && (
					<div className='flex flex-col gap-2'>
						<p className=' font-medium opacity-70'>
							Declaraciones de años anteriores
						</p>
						<hr />

						<div
							ref={declarationsOverviewRef}
							className='bg-white rounded-lg overflow-hidden'
						>
							<div className='bg-white rounded-2xl shadow-sm p-2 w-full flex flex-col gap-3 md:gap-1'>
								{filteredDeclarations.map((declaration, index, array) => (
									<DeclarationOverviewItem
										key={index}
										declaration={declaration}
										index={index}
										array={array}
										selectedDeclarations={selectedDeclarations}
										setSelectedDeclarations={setSelectedDeclarations}
										setSelectedPresentedDeclaration={
											setSelectedPresentedDeclaration
										}
									/>
								))}
							</div>

							<PurchaseDeclaration
								type={selectedType}
								selectedDeclarations={selectedDeclarations}
								setSelectedDeclarations={setSelectedDeclarations}
							/>
						</div>
					</div>
				)}
			</div>
		);
	}

	if (!declarationsOverviewDetail) return;

	if (page === 'annual' && filteredDeclarations.length > 0) {
		return (
			<div className='flex flex-col gap-2'>
				<p className='bg-h-green-50/10 p-2 rounded-t-xl text-center leading-tight'>
					Queremos ayudarte a presentar tu anual a tiempo,{' '}
					<b>regularízate con descuento para poder presentar tu declaración</b>
				</p>
				<div
					ref={declarationsOverviewRef}
					className='bg-white rounded-lg overflow-hidden'
				>
					<div className='p-3 flex items-start gap-2 border-b-4 border-neutral-100'>
						<div className='leading-tight flex flex-col gap-1'>
							<p className='font-medium flex gap-2 items-center'>
								<div className='size-2 rounded-full bg-red-600 ' /> Tu
								declaración anual está bloqueada
							</p>
							<p className='font-light text-sm leading-tight'>
								Tu declaración anual se encuentra bloqueada porque tienes
								algunos periodos pendientes por declarar del 2024. Por favor
								regulariza cada uno.
							</p>
						</div>
					</div>
					<div className='bg-white rounded-2xl shadow-sm p-2 w-full flex flex-col gap-3 md:gap-1'>
						{filteredDeclarations.map((declaration, index, array) => (
							<DeclarationOverviewItem
								showIncomes={false}
								key={index}
								declaration={declaration}
								index={index}
								array={array}
								selectedDeclarations={selectedDeclarations}
								setSelectedDeclarations={setSelectedDeclarations}
								setSelectedPresentedDeclaration={
									setSelectedPresentedDeclaration
								}
							/>
						))}
					</div>

					<PurchaseDeclaration
						type={selectedType}
						selectedDeclarations={selectedDeclarations}
						setSelectedDeclarations={setSelectedDeclarations}
					/>
				</div>
			</div>
		);
	}

	if (page === 'home') {
		return (
			<>
				<div
					ref={declarationsOverviewRef}
					className='bg-white rounded-2xl shadow-sm p-2 w-full flex flex-col gap-3 md:gap-1'
				>
					<ModalPresentedDeclaration
						selectedPresentedDeclaration={selectedPresentedDeclaration}
						setSelectedPresentedDeclaration={setSelectedPresentedDeclaration}
					/>
					<div className='flex flex-col md:flex-row gap-2 md:gap-0 items-start justify-between p-1'>
						<div className='flex flex-col w-full'>
							<div className='flex items-center justify-between'>
								<div className='w-full'>
									<h3 className='text-lg font-bold text-gray-900 flex items-center gap-1 justify-between w-full'>
										Tus declaraciones
									</h3>
									{declarationsOverviewDetail &&
										declarationsOverviewDetail?.length > 0 && (
											<p className='text-neutral-400 text-sm -mt-0.5'>
												Actualizadas el{' '}
												{getFormatDate(
													declarationsOverviewDetail?.[0]?.updated_at!,
												)}
											</p>
										)}
								</div>
								<div className='relative'>
									<button
										className='size-10 bg-neutral-100 rounded-full flex items-center justify-center md:hidden'
										onClick={() => setShowFilters(!showFilters)}
									>
										<Funnel
											size={20}
											className='text-neutral-500'
										/>
									</button>
									{showFilters && (
										<div className='bg-white rounded-xl w-[300px] shadow-lg absolute top-12 right-0 z-10 p-2 border border-neutral-200 fade-in-scale'>
											<Filters
												className='flex-col !items-start'
												yearsArray={yearsArray}
												selectedYear={selectedYear}
												setSelectedYear={setSelectedYear}
												selectedStatus={selectedStatus}
												setSelectedStatus={setSelectedStatus}
												setSelectedDeclarations={setSelectedDeclarations}
												setShowFilters={setShowFilters}
												selectedType={selectedType}
												setSelectedType={setSelectedType}
											/>
										</div>
									)}
								</div>
							</div>
							{showAllDeclarations && (
								<div className='flex flex-col md:flex-row gap-2 mt-2 justify-between w-full'>
									<div className='hidden md:block'>
										<Filters
											setShowFilters={setShowFilters}
											yearsArray={yearsArray}
											selectedYear={selectedYear}
											setSelectedYear={setSelectedYear}
											selectedStatus={selectedStatus}
											setSelectedStatus={setSelectedStatus}
											setSelectedDeclarations={setSelectedDeclarations}
											selectedType={selectedType}
											setSelectedType={setSelectedType}
										/>
									</div>

									<div className='grid grid-cols-3 md:flex items-center text-sm rounded-xl overflow-hidden border border-neutral-200 p-1 gap-1'>
										<button
											className={`px-2 md:hover:bg-neutral-100 h-7 rounded-lg ${
												selectedStatus === 'all' ? 'bg-neutral-100' : ''
											}`}
											onClick={() => {
												setSelectedStatus('all');
											}}
										>
											Todas
										</button>
										<button
											className={`px-2 md:hover:bg-green-100 h-7 rounded-lg ${
												selectedStatus === 'presented' ? 'bg-green-100' : ''
											}`}
											onClick={() => {
												setSelectedStatus('presented');
											}}
										>
											Presentadas
										</button>
										<button
											className={`px-2 md:hover:bg-red-100 h-7 rounded-lg ${
												selectedStatus === 'pending' ? 'bg-red-100' : ''
											}`}
											onClick={() => {
												setSelectedStatus('pending');
											}}
										>
											Atrasadas
										</button>
									</div>
								</div>
							)}
						</div>
					</div>

					<hr className='opacity-70 !m-0' />

					<div className='flex flex-col gap-2 md:gap-1'>
						{(showAllDeclarations
							? filteredDeclarations?.slice(0, 12)
							: filteredDeclarations?.slice(0, 3)
						).map((declaration, index, array) => (
							<DeclarationOverviewItem
								key={index}
								declaration={declaration}
								index={index}
								array={array}
								selectedDeclarations={selectedDeclarations}
								setSelectedDeclarations={setSelectedDeclarations}
								setSelectedPresentedDeclaration={
									setSelectedPresentedDeclaration
								}
							/>
						))}
						{filteredDeclarations?.length === 0 && (
							<div className='flex justify-center my-4'>
								<p className='text-neutral-400 text-sm'>
									No se encontraron declaraciones
								</p>
							</div>
						)}

						{filteredDeclarations.length > 3 && (
							<div className='flex justify-center mt-4'>
								<button
									onClick={() => {
										setShowAllDeclarations(!showAllDeclarations);
										if (showAllDeclarations) {
											setSelectedDeclarations([]);
										}
									}}
									className={`px-4 py-2  w-full rounded-lg font-medium  transition-colors flex items-center justify-center gap-2 ${
										showAllDeclarations
											? 'text-neutral-900 border border-neutral-200 hover:bg-neutral-50'
											: 'bg-neutral-50 text-neutral-900 hover:bg-blue-100'
									}`}
								>
									{showAllDeclarations
										? 'Mostrar menos'
										: 'Ver todas las declaraciones'}
									{showAllDeclarations ? (
										<CaretUp
											weight='bold'
											size={16}
										/>
									) : (
										<CaretDown
											weight='bold'
											size={16}
										/>
									)}
								</button>
							</div>
						)}
					</div>
				</div>
				{isDeclarationsOverviewRefVisible && (
					<PurchaseDeclaration
						type={selectedType}
						selectedDeclarations={selectedDeclarations}
						setSelectedDeclarations={setSelectedDeclarations}
					/>
				)}
			</>
		);
	}
};
